import {useMemo} from 'react';

import {mapWorkforceDashboardMetricResponse} from '../serializers/workforceDashboardSerializers';
import {
  WorkforceDashboardActionItem,
  WorkforceDashboardAlignmentDistribution,
  WorkforceDashboardAlignmentShape,
  WorkforceDashboardDepartmentHealth,
  WorkforceDashboardDimensionImbalance,
  WorkforceDashboardMetric,
  WorkforceDashboardMetricEnvelope,
  WorkforceDashboardOverview,
  WorkforceDashboardRecurringSkillGapsMetricData,
  WorkforceDashboardRedeploymentCandidate,
  WorkforceDashboardRoleCoverageRow,
} from '../types/workforceDashboardTypes';

import {useApiRead} from '../../../../hooks/useApiRead';
import {GetRoutes} from '../../../../lib/api/GET/GetRoutes';

interface MetricState<TData> {
  data: TData | null;
  isLoading: boolean;
  error: unknown;
}

const buildDashboardMetricPath = (companyId: string, metric: WorkforceDashboardMetric) =>
  GetRoutes.companyWorkforceDashboardMetric(companyId, metric);

const useWorkforceDashboardMetric = <TData>(
  companyId: string,
  metric: WorkforceDashboardMetric,
  refreshKey: number
): MetricState<TData> => {
  const path = buildDashboardMetricPath(companyId, metric);
  const result = useApiRead<WorkforceDashboardMetricEnvelope<TData>>({
    path,
    camelCaseOverride: mapWorkforceDashboardMetricResponse,
    options: {
      enabled: !!companyId,
      queryKey: [path, refreshKey],
    },
  });

  return {
    data: result.data?.data ?? null,
    isLoading: result.isLoading,
    error: result.error,
  };
};

export const useWorkforceDashboardMetrics = (companyId: string, refreshKey: number) => {
  const overview = useWorkforceDashboardMetric<WorkforceDashboardOverview>(companyId, 'overview', refreshKey);
  const alignmentDistribution = useWorkforceDashboardMetric<WorkforceDashboardAlignmentDistribution>(
    companyId,
    'alignment_distribution',
    refreshKey
  );
  const departmentHealth = useWorkforceDashboardMetric<WorkforceDashboardDepartmentHealth[]>(
    companyId,
    'department_health',
    refreshKey
  );
  const recurringSkillGaps = useWorkforceDashboardMetric<WorkforceDashboardRecurringSkillGapsMetricData>(
    companyId,
    'recurring_skill_gaps',
    refreshKey
  );
  const scoreDimensionImbalances = useWorkforceDashboardMetric<WorkforceDashboardDimensionImbalance[]>(
    companyId,
    'score_dimension_imbalances',
    refreshKey
  );
  const redeploymentCandidates = useWorkforceDashboardMetric<WorkforceDashboardRedeploymentCandidate[]>(
    companyId,
    'redeployment_candidates',
    refreshKey
  );
  const profilesRequiringAction = useWorkforceDashboardMetric<WorkforceDashboardActionItem[]>(
    companyId,
    'profiles_requiring_action',
    refreshKey
  );
  const roleCoverageHeatmap = useWorkforceDashboardMetric<WorkforceDashboardRoleCoverageRow[]>(
    companyId,
    'role_coverage_heatmap',
    refreshKey
  );
  const alignmentShape = useWorkforceDashboardMetric<WorkforceDashboardAlignmentShape>(
    companyId,
    'alignment_shape',
    refreshKey
  );

  const isLoading = useMemo(
    () =>
      overview.isLoading ||
      alignmentDistribution.isLoading ||
      departmentHealth.isLoading ||
      recurringSkillGaps.isLoading ||
      scoreDimensionImbalances.isLoading ||
      redeploymentCandidates.isLoading ||
      profilesRequiringAction.isLoading ||
      roleCoverageHeatmap.isLoading ||
      alignmentShape.isLoading,
    [
      overview.isLoading,
      alignmentDistribution.isLoading,
      departmentHealth.isLoading,
      recurringSkillGaps.isLoading,
      scoreDimensionImbalances.isLoading,
      redeploymentCandidates.isLoading,
      profilesRequiringAction.isLoading,
      roleCoverageHeatmap.isLoading,
      alignmentShape.isLoading,
    ]
  );

  return {
    overview,
    alignmentDistribution,
    departmentHealth,
    recurringSkillGaps,
    scoreDimensionImbalances,
    redeploymentCandidates,
    profilesRequiringAction,
    roleCoverageHeatmap,
    alignmentShape,
    isLoading,
  };
};
